// Inter is bundled with the app (Final Implementation Authority §25.2): these CSS entries point
// at local woff2 files that Vite copies into the build — the app never fetches a font at runtime.
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
// Display face (restyle 2026-10-18): titles, greetings, the focus card. Bundled like Inter, never fetched.
import '@fontsource/manrope/latin-700.css';
import '@fontsource/manrope/latin-800.css';
import { createRoot } from 'react-dom/client';
import { AppShell } from './app/AppShell';
import { markIPad, markWebVersion } from './app/device';
import { armLaunchAnimation } from './app/launch';
import { resetNav } from './app/navigation';
import { initAppLanguage, setAppLanguage, t } from './i18n/i18n';
import { isAppLocale } from './i18n/locales';
import { installAiJobPushHandling } from './features/ai/pushDeepLink';
import { installAndroidBackButton } from './native/androidBackButton';
import { signOutOfGoogle } from './native/googleSignIn';
import { requireAccountScope } from './persistence/accountScope';
import { enterAccount, leaveAccount } from './state/accountLifecycle';
import { startAiJobsSync } from './state/aiJobsStore';
import { startAgreementPdfKeeper } from './state/agreementPdfStore';
import { jsPdfRenderer } from './report/pdf';
import { unregisterPushOnSignOut } from './state/pushRegistration';
import { initSession, registerAccountHooks, sessionStore, signOutAction } from './state/sessionStore';
import { registerDeviceRevokedHandler } from './state/accountStore';
import { captureWebPurchaseReturn, resumeWebPurchaseReturn } from './state/webPurchaseReturn';
import { showToast } from './ui/components';
import { isRemoteSignToken, REMOTE_SIGN_PATH_PREFIX } from '../supabase/functions/_shared/remoteSignProtocol';
import { RemoteSignRoot } from './features/remoteSign/RemoteSignRoot';
import './ui/tokens.css';
import './ui/theme.css';
import './ui/screens.css';

// Boot order: App Language first (persisted preference → device language → release default, before
// first paint), then the session — WHO is signed in decides WHICH data exists: the account's own
// database is opened and hydrated inside initSession (account isolation, Owner device test
// 2026-09-21; only V2-owned state — persistence isolation is an owner ruling), and a signed-out
// device hydrates nothing. Then land on sign-in or straight into work. Sign-in is required: there is
// no local / guest entry (task book 2026-09-04 §4); a valid remembered session continues offline.
async function boot(): Promise<void> {
  // Before anything renders: an iPad and the web version keep their layout exactly as it was — the foldable rules in
  // theme.css skip them (src/app/device.ts).
  markIPad();
  markWebVersion();
  initAppLanguage();
  // A client opening a remote-signing link (Owner items 4 / 45): the signing page alone — no account, no
  // inspector data, no session is ever touched.
  const signToken = window.location.pathname.startsWith(REMOTE_SIGN_PATH_PREFIX) ? window.location.pathname.slice(REMOTE_SIGN_PATH_PREFIX.length).split('/')[0] : '';
  if (isRemoteSignToken(signToken)) {
    const rootElement = document.getElementById('root');
    if (!rootElement) throw new Error('missing #root');
    createRoot(rootElement).render(<RemoteSignRoot token={signToken} />);
    return;
  }
  // Web version (item 39): back from the hosted payment page? Read and clean the address before anything else.
  const purchaseReturn = captureWebPurchaseReturn();
  // From this line on no database and no inspector profile can be touched without an account.
  requireAccountScope();
  registerAccountHooks({ enter: enterAccount, leave: leaveAccount, signingOut: unregisterPushOnSignOut, signedOut: signOutOfGoogle });
  // Owner decision list 2026-10-19 item 50 (L-5): a device a newer one removed signs out at once.
  registerDeviceRevokedHandler(signOutAction);
  await initSession();
  const phase = sessionStore.get().phase;
  resetNav(phase === 'signedIn' ? { screen: 'home' } : { screen: 'signIn' });
  if (purchaseReturn && phase === 'signedIn') {
    // Straight to the plan page, which says "being confirmed" until the server has seen the purchase.
    resetNav({ screen: 'settingsPage', page: 'plan' });
    void resumeWebPurchaseReturn({ onConfirmed: () => showToast(t('billing.webPurchaseConfirmed')) });
  }
  // Advanced AI jobs (Owner contract 2026-09-13): keep asking the server about unfinished jobs
  // (on start, on resume, on a timer while any job runs) and wire the notification deep link.
  // Both are idle until there is something to do.
  startAiJobsSync();
  installAiJobPushHandling();
  // Owner 2026-10-21: the signed agreement's PDF is made in the background as soon as both parties signed
  // (and again only when the agreement changes), so the final report uses it as it is.
  startAgreementPdfKeeper(jsPdfRenderer);
  // Android QA shell only (no-op elsewhere): system Back closes the open overlay, then one screen up.
  installAndroidBackButton();
  if (import.meta.env.DEV && window.location.hash.startsWith('#foundation-gallery')) {
    // Development-only visual harness: #foundation-gallery or #foundation-gallery/en-US.
    const requested = window.location.hash.split('/')[1];
    if (isAppLocale(requested)) setAppLanguage(requested);
    resetNav({ screen: 'foundationGallery' });
  }
  if (import.meta.env.DEV && window.location.hash.startsWith('#dev-scenario')) {
    // Development-only visual-gate scenarios: #dev-scenario/<screen>/<lang> (src/app/devScenarios.ts).
    // Dynamic import: dead code in production builds, exactly like the foundation gallery.
    const { runDevScenario } = await import('./app/devScenarios');
    await runDevScenario(window.location.hash);
  }
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('missing #root');
  // Launch motion on a cold start only, and never over a development scenario or the gallery
  // (they are captured for review and must show the screen itself).
  if (!window.location.hash.startsWith('#dev-scenario') && !window.location.hash.startsWith('#foundation-gallery')) armLaunchAnimation();
  createRoot(rootElement).render(<AppShell />);
}

void boot();
